import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  DestroyRef,
  viewChildren,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import {
  MatExpansionPanel,
  MatAccordion,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelContent,
} from '@angular/material/expansion';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';

import {
  ControlTowerService,
  DigidocsApiService,
  DigidocsListItem,
  LoadingService,
  UserRolesEnum,
  WorkApprovalStatusEnum,
} from '@eagle/shared';
import { AuthService } from '@eagle/shared';
import { environment } from '@eagle/env';
import { MatchingDataService } from '../matching/services';
import { GoogleAnalyticsService } from '../../core';
import { ContactTypeEnum } from '@eagle/app/shared/components/contact-action/components/contact-button/contact-button.component';
import moment from 'moment';
import { DatetimeFormats } from '@eagle/app/shared/enums';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatDivider } from '@angular/material/divider';
import { MatBadge } from '@angular/material/badge';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatToolbar } from '@angular/material/toolbar';
import { NavigationHistoryComponent } from '../../shared/components/navigation-history/components/navigation-history/navigation-history.component';
import { LanguageSwitchComponent } from '../../shared/components/language-switch/language-switch.component';
import { MatProgressBar } from '@angular/material/progress-bar';
import { UnansweredSmsListComponent } from '../../shared/components/unanswered-sms-list/unanswered-sms-list.component';
import { ProcessingCompanyListComponent } from '../../shared/components/processing-company-list/components/processing-company-list/processing-company-list.component';
import { SafetyAllocationListComponent } from '../../shared/components/safety-allocation-list/components/safety-allocation-list/safety-allocation-list.component';
import {
  MatTable,
  MatColumnDef,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatNoDataRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { ContactButtonComponent } from '../../shared/components/contact-action/components/contact-button/contact-button.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DateTimePipe } from '../../shared/pipes/date-time.pipe';

const LAST_2_WEEKS_DATE = moment().subtract(2, 'weeks').format(DatetimeFormats.ISO_DATE);

@Component({
  selector: 'wlnob-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatSidenavContainer,
    NgClass,
    MatSidenav,
    MatIconButton,
    MatRipple,
    MatTooltip,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    TranslateModule,
    MatDivider,
    MatBadge,
    MatSidenavContent,
    CdkScrollable,
    MatToolbar,
    NavigationHistoryComponent,
    LanguageSwitchComponent,
    MatProgressBar,
    RouterOutlet,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    UnansweredSmsListComponent,
    ProcessingCompanyListComponent,
    SafetyAllocationListComponent,
    MatExpansionPanelContent,
    MatTable,
    MatColumnDef,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    NgTemplateOutlet,
    MatNoDataRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    ContactButtonComponent,
    NgxSkeletonLoaderModule,
    AsyncPipe,
    DateTimePipe,
  ],
})
export class LayoutComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  matchingDataService = inject(MatchingDataService);
  authService = inject(AuthService);
  controlTowerService = inject(ControlTowerService);
  private loadingService = inject(LoadingService);
  private router = inject(Router);
  private gaService = inject(GoogleAnalyticsService);
  private digidocsApiService = inject(DigidocsApiService);

  isLoading$: Observable<boolean>;
  readonly contactTypeEnum = ContactTypeEnum;
  readonly workApprovalStatusEnum = WorkApprovalStatusEnum;
  environmentTag: string;
  readonly brightStaffingUrl = environment.brightStaffingUrl;
  isLeftMenuCollapsed = true;
  isRightMenuCollapsed = true;

  numberOfApprovalToWorkAllocations: number;
  numberOfSafetyDispatchAllocations: number;
  numberOfConditionalApprovedAllocations: number;
  numberOfUnansweredSMS: number;

  readonly isControlTowerItemVisible$ = this.authService.hasRoles$([
    UserRolesEnum.FULL_ADMIN,
    UserRolesEnum.SUPER_ADMIN,
    UserRolesEnum.SALES_ADMIN,
    UserRolesEnum.PREVENTION_ADVISOR,
    UserRolesEnum.CREDIT_CONTROLLER,
  ]);
  readonly missingSignaturesTableColumns: string[] = [
    'deadline',
    'employee',
    'company',
    'function',
  ];
  readonly missingSignaturesDigidocsTotalCount$ = this.digidocsApiService
    .getDocs({
      size: 1,
      createdAfter: LAST_2_WEEKS_DATE,
    })
    .pipe(
      map(resp => resp.totalElements),
      shareReplay({ refCount: true, bufferSize: 1 })
    );
  readonly missingSignatureDigidocs$ = this.digidocsApiService
    .getDocs({
      createdAfter: LAST_2_WEEKS_DATE,
    })
    .pipe(
      map(resp => resp.content),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  readonly rightMenuPanels = viewChildren(MatExpansionPanel);

  constructor() {
    if (environment.ga.trackId) {
      // register google tag manager
      const gTagManagerScript = document.createElement('script');
      gTagManagerScript.async = true;
      gTagManagerScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.ga.trackId}`;
      document.head.appendChild(gTagManagerScript);

      // register google analytics
      const gaScript = document.createElement('script');
      gaScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag() { dataLayer.push(arguments); }
      gtag('js', new Date());
      gtag('config', '${environment.ga.trackId}');
    `;
      document.head.appendChild(gaScript);
    }
  }

  navigateToAllocation(item: DigidocsListItem): void {
    if (!item.allocationId) return;

    this.toggleRightMenu(false);
    this.router.navigate(['/allocations', item.allocationId]);
  }

  ngOnInit(): void {
    this.environmentTag = environment.tag;

    this.isLoading$ = this.loadingService.getIsLoading();

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        tap((e: NavigationEnd) => {
          this.gaService.logPageView(e.url);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  toggleLeftMenu(): void {
    this.isLeftMenuCollapsed = !this.isLeftMenuCollapsed;
  }

  toggleRightMenu(open?: boolean): void {
    this.isRightMenuCollapsed = open != null ? !open : !this.isRightMenuCollapsed;

    if (this.isRightMenuCollapsed) {
      this.rightMenuPanels().forEach(p => p.close());
    }
  }

  public logout(): void {
    this.authService.logout();
  }

  onMatchingResume(): void {
    this.authService.currentUser$.pipe(take(1)).subscribe(({ user, defaultOffice }) =>
      this.gaService.logEvent('Click', 'Matching', 'resume', {
        timestamp: new Date(),
        consultantName: [user.firstName, user.lastName].join(' '),
        officeCode: defaultOffice?.shortName,
      })
    );
  }
}
